import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  CircularProgress,
} from '@mui/material';
import ActionBar from '../../components/ActionBar';
import ExportButton from '../../components/ExportButton';
import RefreshButton from '../../components/RefreshButton';
import api from '../../api/client';

interface RomaneioPendente {
  cnped: number;
  xped: string;
  cliente: string;
  nitemped: number;
  cdproduto: string;
  cdcomercial: string;
  qtde_pedido: number;
  qtde_romaneio: number;
  cnlote: number;
  cdlote: string;
  cnrom: number;
  dtrom: string;
  dtprazo: string;
}

const columns = [
  { id: 'cnped', label: 'Cód Pedido' },
  { id: 'xped', label: 'Pedido Cliente' },
  { id: 'cliente', label: 'Cliente' },
  { id: 'nitemped', label: 'Item Pedido' },
  { id: 'cdproduto', label: 'Cód Produto' },
  { id: 'cdcomercial', label: 'Cód Comercial' },
  { id: 'qtde_pedido', label: 'Qtde Pedido', align: 'right' },
  { id: 'qtde_romaneio', label: 'Qtde Romaneio', align: 'right' },
  { id: 'cnlote', label: 'Controle Lote' },
  { id: 'cdlote', label: 'Cód Lote' },
  { id: 'cnrom', label: 'Cód Romaneio' },
  { id: 'dtrom', label: 'Data Romaneio' },
  { id: 'dtprazo', label: 'Data Prazo' },
];

export default function RomaneiosPendentesPage() {
  const [data, setData] = useState<RomaneioPendente[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      // The current tenant (id_empresa) is usually handled by an interceptor or context.
      // But for explicit calls, we might need to get it from context.
      // Let's rely on api config sending it if it's already set up, otherwise we read from local/session storage.
      const empresaId = sessionStorage.getItem('context_empresa_id') || 1; // Fallback
      
      const response = await api.get(`/logistica/romaneios-pendentes?id_empresa=${empresaId}`);
      setData(response.data);
    } catch (err: any) {
      console.error('Erro ao buscar romaneios pendentes:', err);
      setError('Não foi possível carregar os dados.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('pt-BR').format(num);
  };

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
            Romaneios Pendentes
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Itens separados mas não faturados
          </Typography>
        </Box>
        <ActionBar>
          <RefreshButton onClick={fetchData} />
          <ExportButton data={data} columns={columns.map(c => ({ key: c.id, label: c.label }))} filename="romaneios_pendentes" />
        </ActionBar>
      </Box>

      <Paper sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <TableContainer sx={{ flexGrow: 1 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={(column as any).align || 'left'}
                    sx={{ fontWeight: 600, bgcolor: 'background.paper' }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 10 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 10, color: 'error.main' }}>
                    {error}
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 10 }}>
                    Nenhum romaneio pendente encontrado.
                  </TableCell>
                </TableRow>
              ) : (
                data
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <TableRow hover key={index}>
                      <TableCell>{row.cnped}</TableCell>
                      <TableCell>{row.xped}</TableCell>
                      <TableCell>{row.cliente}</TableCell>
                      <TableCell>{row.nitemped}</TableCell>
                      <TableCell>{row.cdproduto}</TableCell>
                      <TableCell>{row.cdcomercial}</TableCell>
                      <TableCell align="right">{formatNumber(row.qtde_pedido)}</TableCell>
                      <TableCell align="right">{formatNumber(row.qtde_romaneio)}</TableCell>
                      <TableCell>{row.cnlote}</TableCell>
                      <TableCell>{row.cdlote}</TableCell>
                      <TableCell>{row.cnrom}</TableCell>
                      <TableCell>{formatDate(row.dtrom)}</TableCell>
                      <TableCell>{formatDate(row.dtprazo)}</TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas por página"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </Paper>
    </Box>
  );
}
